/**
 * Contact Us Page
 * Submits to the backendContactRequest Cloud Function via src/lib/contact-form.ts.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { FirebaseError } from 'firebase/app';
import { PageWrapper } from '@components/layout/page-wrapper';
import { GlassCard } from '@components/primitives/glass-card';
import { PrimaryButton, SecondaryButton } from '@components/primitives/glass-button';
import { IconWrapper } from '@components/primitives/icon';
import { EnvelopeSimple, Check, WarningCircle } from '@phosphor-icons/react';
import { colors } from '@config/seafoam_colors';
import { spacing, borderRadius } from '@config/spacing';
import { typography } from '@config/typography';
import { useNavigate } from 'react-router-dom';
import { createLogger, trackFormSubmitted } from '@logging';
import { submitContactForm } from '@lib/contact-form';

const logger = createLogger('ContactUsPage');

/* Length bounds mirror server-side validation in
   home-ceo-backend/functions/typescript/src/callables/backend-contact-request.ts */
const NAME_MAX = 100;
const EMAIL_MAX = 320;
const MESSAGE_MAX = 5000;

const ContactContainer = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing['5xl']} ${spacing.xl};
`;

const ContactCard = styled(GlassCard)`
  background: ${colors.white};
  gap: ${spacing.xl};
  max-width: 600px;
  padding: ${spacing['4xl']};
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  text-align: center;
  align-items: center;
`;

const Headline = styled.h1`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: 0;
`;

const Description = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  color: ${colors.textSecondary};
  line-height: ${typography.lineHeight.bodyRelaxed};
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

const Label = styled.label`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text};
`;

const Input = styled.input`
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: ${colors.text};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.12);
  }

  &::placeholder {
    color: ${colors.textMuted};
  }
`;

const TextArea = styled.textarea`
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: ${colors.text};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  min-height: 150px;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.12);
  }

  &::placeholder {
    color: ${colors.textMuted};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${spacing.md};
  width: 100%;
  margin-top: ${spacing.md};
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.lg};
  border-radius: ${borderRadius.md};
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.3);
  color: ${colors.text};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.md} ${spacing.lg};
  border-radius: ${borderRadius.md};
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: ${colors.text};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
`;

export const ContactUsPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    /* Trim + non-empty check. HTML `required` catches missing values but
       not whitespace-only ones; backend trims and rejects 0-length, so
       mirror that here for fast feedback. */
    const name = formData.name.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();
    if (!name || !email || !message) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    /* Round-trip time for the callable, measured from here rather than from
       the click so it reflects the backend call and not how long the visitor
       spent typing. Same convention as the newsletter overlay. */
    const startedAt = performance.now();

    try {
      await submitContactForm({ name, email, message });
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      /* Form id and outcome only — never the name, address or message body.
         The catalog would strip them, but not sending them is the contract. */
      trackFormSubmitted('contact_us', 'succeeded', performance.now() - startedAt);
    } catch (err) {
      /* First statement in the catch, before the error-code branching, so
         every failure path reports exactly once. Two of the branches below
         (`invalid-argument`, `internal`) set a message and log nothing, so
         placing this per-branch would have re-created the gap this fixes. */
      trackFormSubmitted('contact_us', 'failed', performance.now() - startedAt);
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'functions/invalid-argument':
            setErrorMessage(err.message);
            break;
          case 'functions/unauthenticated':
          case 'functions/permission-denied':
            /* App Check rejected. Real users almost never see this; if it
               surfaces, it's usually misconfiguration on this client (debug
               token unregistered, site key mismatch). */
            setErrorMessage('Submission failed, please reach out to support@homeceo.ai');
            logger.error('App Check rejected:', err);
            break;
          case 'functions/internal':
            setErrorMessage('Something went wrong. Please try again.');
            break;
          default:
            setErrorMessage('Unexpected error. Please try again.');
            logger.error('Unknown Firebase error:', err);
        }
      } else {
        setErrorMessage('Unexpected error. Please try again.');
        logger.error('Non-Firebase error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper contained>
      <ContactContainer>
        <ContactCard>
          <Header>
            <IconWrapper $size={48} $color={colors.white}>
              <EnvelopeSimple weight="fill" />
            </IconWrapper>
            <Headline>Contact Us</Headline>
            <Description>
              Have questions about your privacy, data, or our services?
              We&apos;re here to help. Send us a message and we&apos;ll get back to you soon.
            </Description>
          </Header>

          {submitted ? (
            <SuccessMessage>
              <IconWrapper $size={24} $color="#4CAF50">
                <Check weight="bold" />
              </IconWrapper>
              <span>
                Thanks — we received your message and will be in touch.
              </span>
            </SuccessMessage>
          ) : (
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Name *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  maxLength={NAME_MAX}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  maxLength={EMAIL_MAX}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Message *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us how we can help..."
                  maxLength={MESSAGE_MAX}
                  required
                />
              </FormGroup>

              {errorMessage && (
                <ErrorMessage>
                  <IconWrapper $size={20} $color="#EF4444">
                    <WarningCircle weight="fill" />
                  </IconWrapper>
                  <span>{errorMessage}</span>
                </ErrorMessage>
              )}

              <Actions>
                <PrimaryButton type="submit" disabled={loading}>
                  {loading ? 'Sending…' : 'Send Message'}
                </PrimaryButton>
                <SecondaryButton type="button" onClick={() => navigate(-1)} disabled={loading}>
                  Cancel
                </SecondaryButton>
              </Actions>
            </Form>
          )}
        </ContactCard>
      </ContactContainer>
    </PageWrapper>
  );
};
